import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, normalizeClass as _normalizeClass, createElementVNode as _createElementVNode, Teleport as _Teleport, normalizeStyle as _normalizeStyle, createElementBlock as _createElementBlock, Fragment as _Fragment, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = (n: any) => (_pushScopeId("data-v-ff1b8952"),n=n(),_popScopeId(),n)
const _hoisted_1 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("h3", { class: "announcement-title" }, "System Analysis & Update", -1))
const _hoisted_2 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("p", { class: "announcement-text" }, " We've deployed new analytical tools and corrections to your workflow. You now have access to advanced AI-assisted corrections and new ranking metrics. ", -1))

import { ref, reactive, computed, onMounted, onUnmounted, watch } from 'vue';
import { useRoute } from 'vue-router';
import { useTutorialManager } from './composables/useTutorialManager';
import type { MediaOptions } from './types';
import { usePageDetection } from './composables/usePageDetection';

import MascotDisplay from './components/MascotDisplay.vue';
import MessageBox from './components/MessageBox.vue';
import TutorialMenu from './components/TutorialMenu.vue';
import InteractionOverlay from './components/InteractionOverlay.vue';
import MascotMenu from './components/MascotMenu.vue';
import FeedbackModal from './components/FeedbackModal.vue';
import ChoiceBox from './components/ChoiceBox.vue';
import { useFeedback } from './composables/useFeedback';

import type { PecTutorConfig, MascotState, MessageOptions, Tutorial, PecTutorAPI, ChoiceOption } from './types';

// Props
interface Props {
  config?: Partial<PecTutorConfig>;
  autoShow?: boolean;
}

const CURADORIA_ANNOUNCEMENT_COOKIE = 'curadoria_announcement_read';


export default /*#__PURE__*/_defineComponent({
  __name: 'PecTutor',
  props: {
    config: {},
    autoShow: { type: Boolean, default: true }
  },
  setup(__props: any, { expose: __expose }) {

const props = __props;

const route = useRoute();

// Load default config
const defaultConfig = {
  theme: {
    primaryColor: "#007bff",
    secondaryColor: "#6c757d", 
    backgroundColor: "#ffffff",
    textColor: "#333333",
    borderRadius: "8px"
  },
  timeouts: {
    defaultMessage: 5000,
    tipFadeOut: 3000,
    userInputReminder: 10000,
    attentionShake: 1000
  },
  animation: {
    mascotTransition: 300,
    messageAppear: 200,
    shakeIntensity: 10,
    fadeOutDuration: 500
  },
  behavior: {
    autoHide: true,
    rememberProgress: true,
    showMenuOnLoad: false,
    enableSounds: false
  },
  mascot: {
    defaultPosition: "sidebar",
    size: {
      width: 120,
      height: 120
    }
  }
};

// Reactive state
const isVisible = ref(props.autoShow);
const showMenu = ref(false);
const showMascotMenu = ref(false);
const showFeedbackModal = ref(false);
const currentPath = ref(window.location.pathname);
const currentChoice = ref<{
  title: string;
  choices: ChoiceOption[];
  onSelect?: (choice: ChoiceOption) => void;
} | null>(null);
const currentMessage = ref<{
  text: string;
  type: string;
  duration?: number;
  media?: {
    type: string;
    src: string;
    caption?: string;
    controls?: boolean;
    autoplay?: boolean;
  };
  onClose?: () => void;
  isQuestion?: boolean;
  onResponse?: (response: string) => void;
} | null>(null);
const isInteracting = ref(false);
const currentInteraction = ref<{
  type: string;
  selector?: string;
  from?: string;
  to?: string;
  duration?: number;
  options?: Record<string, unknown>;
  onComplete?: () => void;
} | null>(null);

// Announcement logic
const showAnnouncement = ref(false);

const isSidebarAvailable = ref(false);
const teleportTarget = ref<string | Element>('#pectutor-sidebar-target');
const hasTeleportedSuccessfully = ref(false);

const checkSidebarPresence = () => {
  const sidebarTarget = document.getElementById('pectutor-sidebar-target');
  if (sidebarTarget) {
    isSidebarAvailable.value = true;
    teleportTarget.value = sidebarTarget; // Use actual element for more stability
    hasTeleportedSuccessfully.value = true;
    return true;
  }
  
  const taskbarTarget = document.getElementById('pectutor-taskbar-target');
  if (taskbarTarget) {
    isSidebarAvailable.value = true;
    teleportTarget.value = '#pectutor-taskbar-target';
    return true;
  }
  
  isSidebarAvailable.value = false;
  return false;
};

const getCookie = (name: string) => {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop()?.split(';').shift();
  return undefined;
};

const setCookie = (name: string, value: string, days: number) => {
  const date = new Date();
  date.setTime(date.getTime() + (days * 24 * 60 * 60 * 1000));
  const expires = `expires=${date.toUTCString()}`;
  document.cookie = `${name}=${value};${expires};path=/`;
};

const checkAnnouncement = () => {
  if (!route.path.startsWith('/platform/check')) {
    showAnnouncement.value = false;
    return;
  }
  const now = new Date();
  const activePeriodStart = new Date('2024-01-01T00:00:00Z');
  const activePeriodEnd = new Date('2026-12-31T23:59:59Z');

  const isWithinPeriod = now >= activePeriodStart && now <= activePeriodEnd;
  const hasRead =
    getCookie(CURADORIA_ANNOUNCEMENT_COOKIE) === 'true' ||
    getCookie('announcement_read') === 'true';

  if (isWithinPeriod && !hasRead) {
    showAnnouncement.value = true;
  } else {
    showAnnouncement.value = false;
  }
};

const closeAnnouncement = () => {
  showAnnouncement.value = false;
  setCookie(CURADORIA_ANNOUNCEMENT_COOKIE, 'true', 365);
  setCookie('announcement_read', 'true', 365);
};

watch(
  () => route.path,
  () => {
    checkAnnouncement();
  }
);

// Merge configuration
const config = computed(() => ({ ...defaultConfig, ...props.config }) as PecTutorConfig);

// Mascot state
const mascotState = reactive<MascotState>({
  current: 'idle',
  position: { x: 0, y: 0 },
  visible: true
});

// Composables
const { 
  tutorials: availableTutorials, 
  // currentTutorial, // Currently unused
  registerTutorial,
  startTutorial: startTutorialAction,
  stopTutorial
} = useTutorialManager();

const { sendFeedback } = useFeedback();
const { setupPageChangeDetection } = usePageDetection();

/*
const {
  // Visual interaction logic is now handled by InteractionOverlay.vue
} = useInteraction();
*/

// const {
//   getCurrentPage, // Currently unused
//   getTutorialsForPage // Currently unused
// } = usePageDetection();

// Computed styles
const containerStyles = computed(() => ({
  '--primary-color': config.value.theme.primaryColor,
  '--secondary-color': config.value.theme.secondaryColor,
  '--background-color': config.value.theme.backgroundColor,
  '--text-color': config.value.theme.textColor,
  '--border-radius': config.value.theme.borderRadius,
}));

// Check if tutorials are available for current page
const hasAvailableTutorials = computed(() => {
  const currentPageTutorials = availableTutorials.value.filter((tutorial: Tutorial) => 
    tutorial.pageUrl === currentPath.value
  );
  
  return currentPageTutorials.length > 0;
});

// Get tutorials for current page
const currentPageTutorials = computed(() => 
  availableTutorials.value.filter((tutorial: Tutorial) => 
    tutorial.pageUrl === currentPath.value
  )
);

// Methods
const onMascotClick = () => {
  showMascotMenu.value = !showMascotMenu.value;
};

const openTutorials = () => {
  showMascotMenu.value = false;
  showMenu.value = true;
}

const openFeedbackModal = () => {
  showMascotMenu.value = false;
  showFeedbackModal.value = true;
};

const handleFeedbackSubmit = async (feedback: string) => {
  await sendFeedback(feedback);
  showFeedbackModal.value = false;
  // Optionally, show a "thank you" message
  say("Obrigado pelo seu feedback!", { type: 'success', duration: 3000 });
};

const closeMessage = () => {
  // Call the onClose callback if it exists
  if (currentMessage.value?.onClose) {
    currentMessage.value.onClose();
  }
  
  currentMessage.value = null;
  mascotState.current = 'idle';
};

const handleResponse = (response: string) => {
  // Handle user responses to questions
  // Call the onResponse callback if it exists (for questions)
  if (currentMessage.value?.onResponse) {
    currentMessage.value.onResponse(response);
  }
  
  // Clear the message without calling onClose (since onResponse was called)
  currentMessage.value = null;
  mascotState.current = 'idle';
};

const startTutorial = async (tutorialId: string) => {
  showMenu.value = false;
  mascotState.current = 'tutorial';
  await startTutorialAction(tutorialId);
};

const choice = async (title: string, choices: ChoiceOption[]): Promise<void> => {
  return new Promise((resolve) => {
    currentChoice.value = {
      title,
      choices,
      onSelect: (selectedChoice: ChoiceOption) => {
        currentChoice.value = null;
        selectedChoice.action();
        resolve();
      },
    };
  });
};

// API Methods (exposed through template ref)
const say = async (text: string, options: MessageOptions = {}): Promise<void> => {
  return new Promise((resolve) => {
    mascotState.current = 'speaking';
    currentMessage.value = {
      text,
      type: options?.type || 'normal',
      duration: options?.duration || config.value.timeouts.defaultMessage,
      media: options?.media, // Add media support
      onClose: () => {
        resolve();
      }
    };
  });
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const ask = async (question: string, _options: MessageOptions = {}): Promise<string> => {
  return new Promise((resolve) => {
    mascotState.current = 'listening';
    currentMessage.value = {
      text: question,
      type: 'question',
      isQuestion: true,
      onResponse: (response: string) => {
        resolve(response);
        closeMessage();
      }
    };
  });
};

const click = async (selector: string, options: Record<string, unknown> = {}): Promise<void> => {
  return new Promise(resolve => {
    isInteracting.value = true;
    currentInteraction.value = { 
      type: 'click', 
      selector, 
      options,
      onComplete: () => {
        isInteracting.value = false;
        currentInteraction.value = null;
        resolve();
      }
    };
  });
};

const clickAndDrag = async (from: string, to: string, options: Record<string, unknown> = {}): Promise<void> => {
  return new Promise(resolve => {
    isInteracting.value = true;
    currentInteraction.value = { 
      type: 'drag', 
      from, 
      to, 
      options,
      onComplete: () => {
        isInteracting.value = false;
        currentInteraction.value = null;
        resolve();
      }
    };
  });
};

const highlight = async (selector: string, duration = 2000): Promise<void> => {
  return new Promise(resolve => {
    isInteracting.value = true;
    currentInteraction.value = {
      type: 'highlight',
      selector,
      duration,
      onComplete: () => {
        isInteracting.value = false;
        currentInteraction.value = null;
        resolve();
      }
    };
  });
};

const showImage = (src: string, options: MediaOptions = {}) => {
  return say(options.caption || 'Image', {
    duration: options.duration,
    type: 'normal',
    media: {
      type: 'image',
      src: src,
      caption: options.caption,
    },
  });
};

const showVideo = (src: string, options: MediaOptions = {}) => {
  return say(options.caption || 'Video', {
    duration: options.duration,
    type: 'normal',
    media: {
      type: 'video',
      src: src,
      caption: options.caption,
      controls: options.controls,
      autoplay: options.autoplay,
    },
  });
};

const waitForInput = async (selector: string, options: Record<string, unknown> = {}): Promise<boolean> => {
  return new Promise((resolve) => {
    const element = document.querySelector(selector);
    if (!element) {
      console.warn(`PecTutor: Element not found for waitForInput: ${selector}`);
      resolve(false);
      return;
    }

    highlight(selector, 500);

    const timeout = options.timeout as number || 30000;
    const reminderInterval = options.reminderInterval as number || 5000;
    const reminderMessage = options.reminderMessage as string || `Please interact with the highlighted element: ${selector}`;

    // eslint-disable-next-line prefer-const
    let timeoutId: number;
    // eslint-disable-next-line prefer-const
    let reminderIntervalId: number;
    let isReminderActive = false;
    let hasInteracted = false;

    const cleanup = () => {
      clearTimeout(timeoutId);
      clearInterval(reminderIntervalId);
      // Remove listeners from element and all its children
      element.removeEventListener('click', onInteraction, true);
      element.removeEventListener('mousedown', onInteraction, true);
      element.removeEventListener('input', onInteraction, true);
      element.removeEventListener('change', onInteraction, true);
      element.removeEventListener('focus', onInteraction, true);
    };

    const onInteraction = () => {
      if (hasInteracted) return;
      hasInteracted = true;
      cleanup();
      resolve(true);
    };

    // Add listeners with capture=true to catch events on child elements too
    element.addEventListener('click', onInteraction, { capture: true, once: false });
    element.addEventListener('mousedown', onInteraction, { capture: true, once: false });
    element.addEventListener('input', onInteraction, { capture: true, once: false });
    element.addEventListener('change', onInteraction, { capture: true, once: false });
    element.addEventListener('focus', onInteraction, { capture: true, once: false });

    timeoutId = window.setTimeout(() => {
      if (hasInteracted) return;
      cleanup();
      if (options.onTimeout) {
        (options.onTimeout as () => void)();
      }
      resolve(false);
    }, timeout);

    reminderIntervalId = window.setInterval(async () => {
      if (!isReminderActive && !hasInteracted) {
        isReminderActive = true;
        await say(reminderMessage, { type: 'attention', duration: 2000 });
        await highlight(selector, 1000);
        isReminderActive = false;
      }
    }, reminderInterval);
  });
};

const show = () => {
  isVisible.value = true;
};

const hide = () => {
  isVisible.value = false;
  showMenu.value = false;
  currentMessage.value = null;
};

// Lifecycle
onMounted(() => {
  checkAnnouncement();
  
  // Check if sidebar target exists
  const checkSidebar = setInterval(() => {
    if (checkSidebarPresence()) {
      clearInterval(checkSidebar);
    }
  }, 1000);

  // Setup listener for page changes
  setupPageChangeDetection((newPage) => {
    currentPath.value = newPage;
    // Re-check sidebar presence on page change
    setTimeout(checkSidebarPresence, 500);
  });

  setTimeout(() => {
    if (!getCookie('pectutor_v2_welcomed')) {
      say("Clique em mim para ver os tutoriais. Todas as novas funcionalidades da v2.1 estão explicadas aqui!", { type: 'attention', duration: 8000 });
      setCookie('pectutor_v2_welcomed', 'true', 365);
    }
  }, 2000);

  // Small delay to ensure all imports are resolved
  setTimeout(() => {
    // Connect this component instance to the global PecTutor API
    const globalAPI = (window as Window & { PecTutor?: PecTutorAPI & { _setComponentInstance?: (instance: unknown) => void } }).PecTutor;
    
    if (globalAPI && globalAPI._setComponentInstance) {
      const componentAPI = {
        say,
        ask,
        choice,
        click,
        clickAndDrag,
        highlight,
        showImage,
        showVideo,
        waitForInput,
        show,
        hide,
        isVisible: () => isVisible.value,
        get isInteracting() { return isInteracting.value; },
        set isInteracting(value) { isInteracting.value = value; },
        get currentInteraction() { return currentInteraction.value; },
        set currentInteraction(value) { currentInteraction.value = value; }
      };
      
      // Connect to global API
      globalAPI._setComponentInstance(componentAPI);
    } else {
      console.warn('PecTutor: Global API not found on window');
    }
  }, 50);
});

onUnmounted(() => {
  // Disconnect from global API
  const globalAPI = (window as Window & { PecTutor?: PecTutorAPI & { _setComponentInstance?: (instance: unknown) => void } }).PecTutor;
  if (globalAPI && globalAPI._setComponentInstance) {
    globalAPI._setComponentInstance(null);
  }
});

// Expose API methods
__expose({
  say,
  ask,
  choice,
  click,
  clickAndDrag,
  highlight,
  showImage,
  showVideo,
  waitForInput,
  show,
  hide,
  isVisible: () => isVisible.value,
  registerTutorial,
  startTutorial: startTutorialAction,
  stopTutorial
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    (isVisible.value)
      ? (_openBlock(), _createElementBlock("div", {
          key: 0,
          class: _normalizeClass(["pec-tutor-container", { 'sidebar-not-ready': !isSidebarAvailable.value && config.value.mascot.defaultPosition === 'sidebar' }]),
          style: _normalizeStyle(containerStyles.value)
        }, [
          (_openBlock(), _createBlock(_Teleport, {
            to: teleportTarget.value,
            disabled: !isSidebarAvailable.value
          }, [
            _createElementVNode("div", {
              class: _normalizeClass(['mascot-wrapper', `position-${config.value.mascot.defaultPosition}`, { 'in-sidebar': isSidebarAvailable.value }])
            }, [
              _createVNode(MascotDisplay, {
                state: mascotState,
                config: config.value,
                "has-available-tutorials": hasAvailableTutorials.value,
                onClick: onMascotClick
              }, null, 8, ["state", "config", "has-available-tutorials"]),
              (showMascotMenu.value)
                ? (_openBlock(), _createBlock(MascotMenu, {
                    key: 0,
                    "has-tutorials": hasAvailableTutorials.value,
                    onOpenTutorials: openTutorials,
                    onOpenFeedback: openFeedbackModal
                  }, null, 8, ["has-tutorials"]))
                : _createCommentVNode("", true)
            ], 2)
          ], 8, ["to", "disabled"])),
          (showFeedbackModal.value)
            ? (_openBlock(), _createBlock(FeedbackModal, {
                key: 0,
                onClose: _cache[0] || (_cache[0] = ($event: any) => (showFeedbackModal.value = false)),
                onSubmit: handleFeedbackSubmit
              }))
            : _createCommentVNode("", true),
          (currentChoice.value)
            ? (_openBlock(), _createBlock(ChoiceBox, {
                key: 1,
                title: currentChoice.value.title,
                choices: currentChoice.value.choices,
                onSelect: currentChoice.value.onSelect
              }, null, 8, ["title", "choices", "onSelect"]))
            : _createCommentVNode("", true),
          (currentMessage.value)
            ? (_openBlock(), _createBlock(MessageBox, {
                key: 2,
                message: currentMessage.value,
                config: config.value,
                onClose: closeMessage,
                onResponse: handleResponse
              }, null, 8, ["message", "config"]))
            : _createCommentVNode("", true),
          (showMenu.value)
            ? (_openBlock(), _createBlock(TutorialMenu, {
                key: 3,
                tutorials: currentPageTutorials.value,
                config: config.value,
                onStartTutorial: startTutorial,
                onClose: _cache[1] || (_cache[1] = ($event: any) => (showMenu.value = false))
              }, null, 8, ["tutorials", "config"]))
            : _createCommentVNode("", true),
          (currentInteraction.value)
            ? (_openBlock(), _createBlock(InteractionOverlay, {
                key: 4,
                config: config.value,
                "interaction-data": currentInteraction.value,
                onComplete: currentInteraction.value.onComplete
              }, null, 8, ["config", "interaction-data", "onComplete"]))
            : _createCommentVNode("", true)
        ], 6))
      : _createCommentVNode("", true),
    (showAnnouncement.value)
      ? (_openBlock(), _createElementBlock("div", {
          key: 1,
          class: "announcement-overlay",
          style: _normalizeStyle(containerStyles.value)
        }, [
          _createElementVNode("div", { class: "announcement-modal" }, [
            _createElementVNode("div", { class: "announcement-content" }, [
              _hoisted_1,
              _hoisted_2,
              _createElementVNode("button", {
                class: "announcement-btn",
                onClick: closeAnnouncement
              }, "Acknowledge")
            ])
          ])
        ], 4))
      : _createCommentVNode("", true)
  ], 64))
}
}

})